import './globals.css';
import { lazy, Suspense, useEffect, useRef, useState, useMemo } from 'react';
import Hero from './components/Home/Hero';
import Navbar from './components/Navbar/Navbar';
import CustomCursor from './components/Cursor/CustomCursor';
// import SectionCursor from './components/Cursor/SectionCursor';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import gsap from 'gsap';
import Lenis from '@studio-freight/lenis';
import quotes from './assets/data/quotes.json';

import ContactMe from './components/Home/ContactMe';

const MyWorkSection = lazy(() => import('./components/Home/black-section/MyWorkSection'));
const MyServices = lazy(() => import('./components/Home/black-section/MyServices'));
const Footer = lazy(() => import('./components/Footer/Footer'));
const ScrollLine = lazy(() => import('./components/ScrollLine/ScrollLine'));

gsap.registerPlugin(ScrollTrigger);

const MARQUEE_ITEMS = [
  "Fullstack Development", "·", "UI Engineering", "·",
  "React & Next.js", "·", "Node.js & APIs", "·",
  "Interactive Experiences", "·", "Motion Design", "·",
];

/* ── dark palette constants ── */
const SMOKE = "#0A0A08";
const INK = "#F5F4F0";
const SUBTLE = "#686560";

function App() {
  const isMobile = useMemo(() => window.innerWidth <= 768, []);

  const lenisRef = useRef(null);
  if (!lenisRef.current) {
    lenisRef.current = new Lenis({
      duration: isMobile ? 0.8 : 1.05,
      easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,
    });
  }
  const lenis = lenisRef.current;

  const [quote] = useState(() => quotes[Math.floor(Math.random() * quotes.length)]);
  const [quoteTilted, setQuoteTilted] = useState(false);

  const visionRef = useRef(null);
  const quoteRef = useRef(null);
  const authorRef = useRef(null);
  const marqueeRef = useRef(null);
  const marquee2Ref = useRef(null);
  /* the scroll-line spans this container (hero-bottom → contact-top) */
  const scrollZoneRef = useRef(null);

  /* ── Lenis + ScrollTrigger loop ── */
  useEffect(() => {
    // Drive Lenis through GSAP's ticker so both run on the same clock
    gsap.ticker.add((t) => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
    lenis.on('scroll', ScrollTrigger.update);
    ScrollTrigger.config({ ignoreMobileResize: true });

    // Pause Lenis while overlay is open so wheel events reach the panel
    const onOpen  = () => lenis.stop();
    const onClose = () => lenis.start();
    window.addEventListener("overlay:open",  onOpen);
    window.addEventListener("overlay:close", onClose);

    const ctx = gsap.context(() => {

      /* Vision heading — word by word */
      if (visionRef.current) {
        const words = visionRef.current.textContent.trim().split(/\s+/);
        visionRef.current.innerHTML = '';
        words.forEach((w, i) => {
          const wrap = document.createElement('span');
          wrap.style.cssText = 'display:inline-block;overflow:hidden;vertical-align:bottom';
          const inner = document.createElement('span');
          inner.textContent = w;
          inner.style.cssText = 'display:inline-block;will-change:transform';
          wrap.appendChild(inner);
          visionRef.current.appendChild(wrap);
          if (i < words.length - 1) visionRef.current.appendChild(document.createTextNode(' '));
        });
        const inners = visionRef.current.querySelectorAll('span > span');
        gsap.fromTo(inners,
          { y: '110%' },
          {
            y: '0%', stagger: 0.055, duration: 1.1, ease: 'power4.out',
            scrollTrigger: { trigger: visionRef.current, start: 'top 85%', toggleActions: 'play none none none' },
            onComplete: () => inners.forEach(el => el.style.willChange = 'auto')
          }
        );
        gsap.set(visionRef.current, { opacity: 1 });
      }

      /* Quote */
      if (quoteRef.current) {
        gsap.fromTo(quoteRef.current, { opacity: 0, y: 36 },
          {
            opacity: 1, y: 0, duration: 1, ease: 'power3.out',
            scrollTrigger: { trigger: quoteRef.current, start: 'top 88%', toggleActions: 'play none none none' }
          }
        );
      }
      if (authorRef.current) {
        gsap.fromTo(authorRef.current, { opacity: 0, y: 18 },
          {
            opacity: 1, y: 0, duration: 0.7, ease: 'power3.out',
            scrollTrigger: { trigger: authorRef.current, start: 'top 92%', toggleActions: 'play none none none' }
          }
        );
      }
    });

    return () => {
      ctx.revert();
      gsap.ticker.remove((t) => lenis.raf(t * 1000));
      window.removeEventListener("overlay:open",  onOpen);
      window.removeEventListener("overlay:close", onClose);
      lenis.off('scroll', ScrollTrigger.update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Marquee scroll-velocity skew ── */
  useEffect(() => {
    const tracks = [marqueeRef.current, marquee2Ref.current].filter(Boolean);
    if (!tracks.length) return;
    let lastScroll = window.scrollY, velocity = 0, skew = 0, raf;
    let visible = false;
    const lerp = (a, b, t) => a + (b - a) * t;
    const tick = () => {
      if (!visible) return;
      const s = window.scrollY;
      velocity = lerp(velocity, s - lastScroll, 0.22);
      lastScroll = s;
      skew = lerp(skew, Math.max(-16, Math.min(16, velocity * 0.75)), 0.1);
      tracks.forEach((t, i) => { if (t) t.style.transform = `skewX(${i === 0 ? -skew : skew}deg)`; });
      raf = requestAnimationFrame(tick);
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) { lastScroll = window.scrollY; raf = requestAnimationFrame(tick); }
      else cancelAnimationFrame(raf);
    }, { rootMargin: "100px" });
    if (marqueeRef.current) observer.observe(marqueeRef.current);
    return () => { cancelAnimationFrame(raf); observer.disconnect(); };
  }, []);

  const marq = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  const [hoveredMarqIdx, setHoveredMarqIdx] = useState(null);

  return (
    <div style={{ background: SMOKE }} className="app-root min-h-screen w-full font-Montreal-Regular overflow-x-hidden">
      <CustomCursor />
      {/* <SectionCursor /> */}
      <Navbar lenis={lenis} />

      {/* ── Hero ── */}
      <Hero lenis={lenis} />

      {/* ── Marquee separator ── */}
      <div className="w-full overflow-hidden select-none" style={{ background: SMOKE, padding: "88px 0", borderTop: "1px solid rgba(245,244,240,0.07)", position: "relative" }}>
        <div ref={marqueeRef}>
          <div className="marquee-track-fast"
            style={{ display: "flex", gap: "clamp(20px,4vw,52px)", paddingRight: "clamp(20px,4vw,52px)" }}>
            {marq.map((item, i) => (
              <span key={i} className="font-Montreal-Bold font-black whitespace-nowrap uppercase"
                onMouseEnter={() => item !== "·" && setHoveredMarqIdx(i)}
                onMouseLeave={() => setHoveredMarqIdx(null)}
                style={{
                  fontSize: "clamp(3.6rem,5.8vw,6.8rem)", letterSpacing: "-0.025em", lineHeight: 1,
                  color: item === "·" ? "rgba(245,244,240,0.12)" : "rgba(245,244,240,0.80)",
                  display: "inline-block",
                  transition: "transform 0.2s cubic-bezier(.25,.46,.45,.94), opacity 0.2s ease",
                  transform: hoveredMarqIdx === i ? "translateY(-6px) skewX(-4deg)" : "translateY(0) skewX(0deg)",
                  opacity: hoveredMarqIdx !== null && hoveredMarqIdx !== i && item !== "·" ? 0.45 : 1,
                }}>
                {item}
              </span>
            ))}
          </div>
        </div>
        <div ref={marquee2Ref} style={{ marginTop: "8px" }}>
          <div className="marquee-track-slow"
            style={{ display: "flex", gap: "clamp(20px,4vw,52px)", paddingRight: "clamp(20px,4vw,52px)" }}>
            {[...marq].reverse().map((item, i) => (
              <span key={i} className="font-Montreal-Bold font-black whitespace-nowrap uppercase"
                style={{
                  fontSize: "clamp(3.6rem,5.8vw,5.8rem)", letterSpacing: "-0.025em", lineHeight: 1,
                  color: item === "·" ? "rgba(245,244,240,0.06)" : "rgba(245,244,240,0.18)"
                }}>
                {item}
              </span>
            ))}
          </div>
        </div>

        {/* gradient fade into services */}
        <div aria-hidden style={{
          position: "absolute", bottom: 0, left: 0, right: 0,
          height: "140px", pointerEvents: "none",
          background: `linear-gradient(to bottom, transparent 0%, ${SMOKE} 100%)`,
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 100%)",
          maskImage: "linear-gradient(to bottom, transparent 0%, black 100%)",
        }} />
      </div>

      {/* ── Projects ── */}
      <Suspense fallback={null}>
        <MyWorkSection />
      </Suspense>

      {/* ── Services — full-bleed dark ── */}
      <Suspense fallback={null}>
        <MyServices />
      </Suspense>


      {/* ─────────────────────────────────────────────────────────
          SMOKE WHITE ZONE: vision + quote + scroll line lives here
          ───────────────────────────────────────────────────────── */}
      <div ref={scrollZoneRef} style={{ background: SMOKE, position: "relative" }}>

        {/* Animated scroll line in left gutter — only desktop */}
        <Suspense fallback={null}>
          <ScrollLine containerRef={scrollZoneRef} lenis={lenis} />
        </Suspense>

        {/* Vision heading */}
        <div style={{
          paddingLeft: "clamp(24px,10vw,120px)", paddingRight: "clamp(24px,6vw,80px)",
          paddingTop: "clamp(80px,12vw,160px)", paddingBottom: "clamp(60px,8vw,100px)"
        }}
        // className='z-20'
        >
          <h2
            ref={visionRef}
            className="font-Montreal-Bold font-black uppercase opacity-0"
            style={{
              fontSize: "clamp(2rem,7vw,7.5vw)", letterSpacing: "-0.03em",
              lineHeight: 1.06, color: INK
            }}
          >
            Let's Build Something That Lasts /
          </h2>
        </div>

        {/* Rule */}
        <div style={{
          margin: "0 clamp(24px,6vw,80px)", height: "1px",
          background: "rgba(245,244,240,0.12)"
        }} />

        {/* Quote */}
        <div style={{
          paddingLeft: "clamp(24px,10vw,120px)", paddingRight: "clamp(24px,6vw,80px)",
          paddingTop: "clamp(60px,8vw,100px)", paddingBottom: "clamp(80px,12vw,160px)",
          maxWidth: "980px"
        }}>
          <blockquote
            onMouseEnter={() => setQuoteTilted(true)}
            onMouseLeave={() => setQuoteTilted(false)}
            style={{
              transition: "transform 0.45s cubic-bezier(.25,.46,.45,.94)",
              transform: quoteTilted ? "rotate(-0.6deg) translateY(-3px)" : "rotate(0deg) translateY(0)",
              transformOrigin: "left center",
              display: "block",
            }}
          >
            <p ref={quoteRef} className="font-Montreal-Bold opacity-0"
              style={{
                fontSize: "clamp(1.3rem,3.2vw,3.2vw)", lineHeight: 1.35,
                color: INK, letterSpacing: "-0.02em"
              }}>
              "{quote.text}"
            </p>
            <footer ref={authorRef} className="mt-6 font-Montreal-Mono text-sm md:text-base opacity-0"
              style={{ color: SUBTLE }}>
              — {quote.author}
            </footer>
          </blockquote>
        </div>
      </div>
      {/* ── end smoke white zone ── */}

      {/* ── Contact — full-bleed dark ── */}
      <ContactMe />

      {/* ── Footer — smoke white ── */}
      <Suspense fallback={null}>
        <Footer lenis={lenis} />
      </Suspense>
    </div>
  );
}

export default App;
